import { useState } from 'preact/hooks';
import { api, mensagemDoErro, salvarBlob } from '../api';
import { plural } from '../format';
import { useApi } from '../hooks';
import type { Projeto, ResultadoImportacao } from '../types';
import { ErroForm, Modal, avisar } from '../ui';

const MAX_BYTES = 2_000_000;
const CABECALHO = 'projeto;municipio;titulo;descricao;status;progresso;inicio;fim_previsto;responsavel';

/** O Excel em português costuma salvar CSV em Windows-1252; lê como UTF-8 e, se falhar, como Windows-1252. */
export async function lerArquivoTexto(arq: File): Promise<string> {
  const bytes = await arq.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

type Props = { onFechar: () => void; onImportado: () => void };

export function ImportarCsvModal({ onFechar, onImportado }: Props) {
  const projetos = useApi<Projeto[]>('/projetos?ativo=true');
  const [csv, setCsv] = useState('');
  const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);
  const [ocupado, setOcupado] = useState<'' | 'validando' | 'importando'>('');
  const [erro, setErro] = useState('');

  function baixarModelo() {
    const exemplo = projetos.dados?.[0]?.titulo ?? 'Nome exato do projeto';
    const linhas = [
      CABECALHO,
      `${exemplo};João Pessoa;Reforma da escola municipal;Troca de telhado e pintura;em andamento;40;01/03/2026;31/12/2026;Maria Silva`,
      `${exemplo};Campina Grande;Construção da praça;;planejada;;;;`,
    ];
    salvarBlob(new Blob(['﻿' + linhas.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' }), 'modelo-importacao-acoes.csv');
  }

  async function aoEscolherArquivo(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const arq = input.files?.[0];
    if (!arq) return;
    setErro('');
    setResultado(null);
    if (arq.size > MAX_BYTES) {
      input.value = '';
      return setErro('O arquivo passa de 2 MB. Divida em partes.');
    }
    setCsv(await lerArquivoTexto(arq));
  }

  async function enviar(confirmar: boolean) {
    setErro('');
    setOcupado(confirmar ? 'importando' : 'validando');
    try {
      const r = await api<ResultadoImportacao>('/acoes/importar', { method: 'POST', body: { csv, confirmar } });
      setResultado(r);
      if (confirmar) {
        avisar(`${plural(r.criadas ?? 0, 'ação importada', 'ações importadas')}.`);
        onImportado();
        onFechar();
      }
    } catch (e) {
      // 422 na confirmação: a API devolve a lista de erros no corpo, mas aqui já a mostramos pela validação anterior.
      setErro(mensagemDoErro(e));
    } finally {
      setOcupado('');
    }
  }

  const pronto = resultado !== null && resultado.comErro === 0 && resultado.validas > 0;

  return (
    <Modal titulo="Importar ações por CSV" onFechar={onFechar} largo>
      <div class="form">
        <p class="dica">
          Colunas: <code>projeto</code>, <code>municipio</code> e <code>titulo</code> são obrigatórias; também aceitamos <code>descricao</code>, <code>status</code>,{' '}
          <code>progresso</code>, <code>inicio</code>, <code>fim_previsto</code> e <code>responsavel</code>. O projeto deve existir, com o título exato, e o município pode ser o nome ou o código IBGE.
          Datas em AAAA-MM-DD ou DD/MM/AAAA. Até 1.000 linhas.
        </p>
        <div class="linha-form alinhar">
          <button type="button" class="botao" onClick={baixarModelo}>Baixar modelo</button>
          <label class="botao arquivo">
            Escolher arquivo CSV
            <input type="file" accept=".csv,text/csv" onChange={aoEscolherArquivo} class="so-leitor-foco" />
          </label>
        </div>

        <label class="campo-form">
          <span>Ou cole o conteúdo aqui</span>
          <textarea rows={8} value={csv} class="mono" spellcheck={false} placeholder={CABECALHO}
            onInput={(e) => { setCsv(e.currentTarget.value); setResultado(null); }} />
        </label>

        <ErroForm texto={erro} />

        {resultado && (
          <section class="resultado-import" aria-live="polite">
            <p>
              <strong>{plural(resultado.total, 'linha lida', 'linhas lidas')}:</strong>{' '}
              <span class="verde-txt">{resultado.validas} válida{resultado.validas === 1 ? '' : 's'}</span>
              {resultado.comErro > 0 && <>, <span class="vermelho-txt">{resultado.comErro} com problema</span></>}.
            </p>
            {resultado.comErro > 0 && (
              <>
                <p class="dica">Nada é importado enquanto houver linha com problema. Corrija o arquivo e valide de novo.</p>
                <div class="tabela-rolagem pequena">
                  <table class="tabela">
                    <thead><tr><th scope="col">Linha</th><th scope="col">Problema</th></tr></thead>
                    <tbody>
                      {resultado.erros.map((er) => (
                        <tr key={er.linha}><td>{er.linha}</td><td>{er.mensagens.length ? er.mensagens.join(' · ') : 'Linha inválida'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="button" class="botao" disabled={!csv.trim() || ocupado !== ''} onClick={() => void enviar(false)}>
            {ocupado === 'validando' ? 'Validando…' : 'Validar'}
          </button>
          <button type="button" class="botao primario" disabled={!pronto || ocupado !== ''} onClick={() => void enviar(true)}>
            {ocupado === 'importando' ? 'Importando…' : pronto ? `Importar ${plural(resultado!.validas, 'ação', 'ações')}` : 'Importar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
